import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { Box, FormHelperText, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { useRedisWritesBlocked } from '@/apis/call';
import { SUPPORTER_LIST_NAME_MAX_LEN } from '@/constants';
import { ApiBodyError, isDefinedError } from '@/errors';
import { CatCampaignRecord, QFilter } from '@/types';
import { SearchTreeNode } from '@/types/searchTreeTypes';
import CampaignAutocomplete from '@components/CampaignAutocomplete';
import { useSaveList } from './hooks';

const LIST_SIZE_LIMIT_MESSAGE = 'Note: only the first 10,000 contacts will be exported.';

type SaveListDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  qFilter?: QFilter;
  tree?: SearchTreeNode;
  contactIds?: number[];
  isTruncateContacts?: boolean;
  onAllowNavigationBeforeLeave?: () => void;
};

/**
 * The SaveListDialog works with either qFilters or (search) trees
 */
const SaveListDialog = ({
  isOpen,
  onClose: handleClose,
  qFilter,
  tree,
  contactIds,
  isTruncateContacts,
  onAllowNavigationBeforeLeave,
}: SaveListDialogProps) => {
  const [listName, setListName] = useState('');
  const [campaign, setCampaign] = useState<CatCampaignRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();
  const { mutateAsync: saveList, isError, error, reset } = useSaveList(
    handleClose,
    onAllowNavigationBeforeLeave
  );

  const handleSaveToListDialogSave = async () => {
    if (writesBlocked) return;
    setIsSaving(true);
    try {
      if (qFilter && tree) {
        enqueueErrorNotification('Both qFilter and tree have been specified, please only use one');
        return;
      }
      if (qFilter)
        await saveList({ name: listName, qFilter, contactIds, campaignId: campaign?.id ?? null });
      if (tree)
        await saveList({ name: listName, tree, contactIds, campaignId: campaign?.id ?? null });
    } catch {
      enqueueErrorNotification('The list could not be saved.');
    }
    setIsSaving(false);
  };

  const getErrorText = () => {
    if (isError && error instanceof ApiBodyError) {
      if (isDefinedError(error)) {
        const { message, name } = error.body.errors[0];
        if (name === 'CreateSupporterListDuplicateNameError')
          return 'The list name has already been taken.';
        return message;
      }
      return 'There was an issue with the backend.';
    }
    return '';
  };

  const disableSaveButton = listName.length < 3 || !campaign;

  return (
    <AgDialog
      dialogTitle="Save my list"
      isOpen={isOpen}
      onClose={handleClose}
      primaryButton={{
        text: 'Save',
        onClick: handleSaveToListDialogSave,
        disabled: disableSaveButton || writesBlocked,
      }}
      data-testid="SaveListDialog"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem"
        }}>
        <TextField
          margin="dense"
          id="name"
          label={'List name'}
          type="text"
          fullWidth
          variant="standard"
          value={listName}
          onChange={(e) => {
            reset();
            setListName(e.target.value);
          }}
          error={isError}
          helperText={getErrorText()}
          disabled={isSaving}
          slotProps={{
            htmlInput: {
              maxLength: SUPPORTER_LIST_NAME_MAX_LEN,
            },
          }}
        />

        <CampaignAutocomplete
          value={campaign}
          onChange={(newValue) => setCampaign(newValue)}
          disabled={isSaving}
          sx={{ width: '100%' }}
        />

        {contactIds && contactIds.length > 0 && (
          <Typography sx={{ fontStyle: 'italic' }}>
            Note: only the contacts you have selected will be saved to this list.
          </Typography>
        )}
        {isTruncateContacts && <FormHelperText error>{LIST_SIZE_LIMIT_MESSAGE}</FormHelperText>}
      </Box>
    </AgDialog>
  );
};

export default SaveListDialog;
